import {
    memo,
    type PropsWithChildren
} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getFormInputProps } from "util/forms";
import When from "ui/support/When";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, type SetupProps } from "./types";
import SetupValue from "./SetupValue";
import Description from "./Description";

const GeneralSetupForm = (props: PropsWithChildren<SetupProps>) => {
    const {
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        selectSetupActiveCompany,
        setUpGroupNameOptions,
        isBootstrapDataLoading,
        bootstrapData,
        children,
        overrideProps,
        ...restProps
    } = props;

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: {
                        xs: 2,
                        sm: 3
                    },
                    alignItems: 'flex-start'
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            lg: 'repeat(2, 1fr)'
                        },
                        gap: 2
                    }}
                >
                    <TextField
                        disabled
                        {...getFormInputProps(FormField.Identifier, restProps)}
                        fullWidth
                        size='small'
                        name={FormField.Identifier}
                        value={values[FormField.Identifier]}
                        label='Key'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    <StatefulSelector
                        disabled
                        {...overrideProps?.[FormField.Mor]}
                        fullWidth
                        disableClearable
                        layout={null}
                        size='small'
                        selectOptions={selectSetupActiveCompany}
                        value={values[FormField.Mor]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Mor, restProps),
                            name: FormField.Mor,
                            label: 'Company'
                        }}
                        onChange={(_, option) => {
                            setFieldTouched(FormField.Mor, true);
                            setFieldValue(
                                FormField.Mor,
                                option,
                                true
                            );
                        }}
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                    />
                      <StatefulSelector
                        fullWidth
                        disableClearable
                        layout={null}
                        size='small'
                        selectOptions={setUpGroupNameOptions}
                        value={values[FormField.GroupName]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.GroupName, restProps),
                            name: FormField.GroupName,
                            label: 'Group Name'
                        }}
                        onChange={(_, option) => {
                            setFieldTouched(FormField.GroupName, true);
                            setFieldValue(
                                FormField.GroupName,
                                option,
                                true
                            );
                        }}
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                    />
                    <When
                        if={values[FormField.CreatedBySystem] !== null}
                        then={(
                            <TextField
                                {...getFormInputProps(FormField.CreatedBySystem, restProps)}
                                fullWidth
                                disabled
                                size='small'
                                name={FormField.CreatedBySystem}
                                value={values[FormField.CreatedBySystem]}
                                label='System'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        )}
                    />
                    <When
                        if={values[FormField.IsDeletable] !== null}
                        then={(
                            <TextField
                                {...getFormInputProps(FormField.IsDeletable, restProps)}
                                fullWidth
                                disabled
                                size='small'
                                name={FormField.IsDeletable}
                                value={values[FormField.IsDeletable]}
                                label='Deletable'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        )}
                    />
                    <TextField
                        {...getFormInputProps(FormField.ValueType, restProps)}
                        fullWidth
                        disabled
                        size='small'
                        name={FormField.ValueType}
                        value={values[FormField.ValueType]}
                        label='Value Type'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    <Description
                        {...props}
                    />
                    <SetupValue
                        {...props}
                    />
                </Box>
            </Box>
            {children}
        </Box>
    );
};

export default memo(GeneralSetupForm);
